import React from "react";
import {
  FolderIcon,
  FolderPlusIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  HomeIcon,
  PencilIcon,
  ArrowUpTrayIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { Folder } from "../types";

interface FolderSidebarProps {
  sidebarOpen: boolean;
  isMobile: boolean;
  setSidebarOpen: (open: boolean) => void;
  folders: Folder[];
  currentFolder: Folder | null;
  folderSearchTerm: string;
  setFolderSearchTerm: (term: string) => void;
  navigateToFolder: (folder: Folder) => void;
  navigateToRoot: () => void;
  openCreateFolderModal: () => void;
  openEditFolderModal: (folder: Folder) => void;
  openMoveFolderModal: (folder: Folder) => void;
  handleDeleteFolder: (folder: Folder) => void;
}

const FolderSidebar: React.FC<FolderSidebarProps> = ({
  sidebarOpen,
  isMobile,
  setSidebarOpen,
  folders,
  currentFolder,
  folderSearchTerm,
  setFolderSearchTerm,
  navigateToFolder,
  navigateToRoot,
  openCreateFolderModal,
  openEditFolderModal,
  openMoveFolderModal,
  handleDeleteFolder,
}) => {
  return (
    <>
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Folder Sidebar */}
      <div
        className={`
        fixed lg:static inset-y-0 left-0 z-50 lg:z-auto
        w-64 bg-gradient-to-br from-white to-gray-50 shadow-xl overflow-y-auto border-r border-gray-200
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
      `}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center justify-start mb-4 w-full px-1">
            <div className="flex items-center flex-1">
              <FolderIcon className="h-5 w-5 lg:h-6 lg:w-6 mr-2 text-indigo-600 flex-shrink-0" />
              <h2 className="text-lg font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent text-left truncate">
                ໝວດໝູ່ໂຟລເດີ
              </h2>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={openCreateFolderModal}
                className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all duration-200 hover:shadow-md"
                title="ສ້າງໂຟລເດີໃໝ່"
              >
                <FolderPlusIcon className="h-4 w-4 lg:h-5 lg:w-5" />
              </button>
              {isMobile && (
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="lg:hidden p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="ປິດເມນູ"
                  aria-label="ປິດເມນູ"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Folder Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="ຄົ້ນຫາໂຟລເດີ..."
              value={folderSearchTerm}
              onChange={(e) => setFolderSearchTerm(e.target.value)}
              className="w-full px-3 lg:px-4 py-2 lg:py-3 pl-8 lg:pl-10 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 shadow-sm"
            />
            <MagnifyingGlassIcon className="absolute left-2 lg:left-3 top-2.5 lg:top-3.5 h-4 w-4 text-gray-400" />
          </div>
        </div>

        {/* Navigation */}
        <div className="p-3 lg:p-4">
          <button
            onClick={navigateToRoot}
            className={`w-full text-left px-3 lg:px-4 py-2 lg:py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
              !currentFolder
                ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg border-0"
                : "text-gray-700 hover:bg-gray-100 hover:shadow-md"
            }`}
          >
            <HomeIcon className="h-4 w-4 inline mr-2 lg:mr-3" />
            <span className="hidden sm:inline">ໜ້າຫຼັກ</span>
          </button>
          <div className="space-y-1 lg:space-y-2 mt-3 lg:mt-4">
            {folders
              .filter((folder) =>
                folder.name
                  .toLowerCase()
                  .includes(folderSearchTerm.toLowerCase()),
              )
              .map((folder, index) => (
                <div
                  key={`sidebar-folder-${folder.id}-${index}`}
                  className="group relative"
                >
                  <div
                    onClick={() => {
                      navigateToFolder(folder);
                      if (isMobile) setSidebarOpen(false);
                    }}
                    className={`w-full text-left px-3 lg:px-4 py-2 lg:py-3 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer ${
                      currentFolder?.id === folder.id
                        ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg border-0"
                        : "text-gray-700 hover:bg-gray-100 hover:shadow-md"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center min-w-0 flex-1 transition-all duration-200 group-hover:flex-[0] group-hover:opacity-0">
                        <FolderIcon className="h-4 w-4 mr-2 lg:mr-3 text-indigo-500 flex-shrink-0" />
                        <span className="truncate text-sm font-medium">
                          {folder.name}
                        </span>
                        <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full flex-shrink-0">
                          {folder.video_count}
                        </span>
                      </div>

                      {/* Folder Actions Menu */}
                      <div className="opacity-0 group-hover:opacity-100 transition-all duration-200 flex-shrink-0 w-0 group-hover:w-auto overflow-hidden transform group-hover:scale-100 scale-95">
                        <div className="flex space-x-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditFolderModal(folder);
                            }}
                            className="text-gray-500 hover:text-indigo-600 p-1.5 rounded-md hover:bg-indigo-50 transition-all duration-200 group/edit"
                            title="ປ່ຽນຊື່ໂຟລເດີ"
                          >
                            <div className="relative">
                              <PencilIcon className="h-3 w-3 transition-all duration-200 group-hover/edit:scale-110" />
                              <div className="absolute inset-0 bg-indigo-100 rounded-full opacity-0 group-hover/edit:opacity-20 transition-opacity duration-200"></div>
                            </div>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openMoveFolderModal(folder);
                            }}
                            className="text-gray-500 hover:text-blue-600 p-1.5 rounded-md hover:bg-blue-50 transition-all duration-200 group/move"
                            title="ຍ້າຍໂຟລເດີ"
                          >
                            <div className="relative">
                              <ArrowUpTrayIcon className="h-3 w-3 transition-all duration-200 group-hover/move:scale-110" />
                              <div className="absolute inset-0 bg-blue-100 rounded-full opacity-0 group-hover/move:opacity-20 transition-opacity duration-200"></div>
                            </div>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFolder(folder);
                            }}
                            className="text-gray-500 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50 transition-all duration-200 group/delete"
                            title="ລຶບໂຟລເດີ"
                          >
                            <div className="relative">
                              <TrashIcon className="h-3 w-3 transition-all duration-200 group-hover/delete:scale-110" />
                              <div className="absolute inset-0 bg-red-100 rounded-full opacity-0 group-hover/delete:opacity-20 transition-opacity duration-200"></div>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default FolderSidebar;
